/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';

import logo from '../../assets/whiteLogo.png';
import api from '../../services/api';

// Iniciais do nome (máx 2 letras) — pra Avatar sem foto.
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

// Header fixo do admin. Recebe callback `onMenuClick` opcional — quando
// definido, mostra hamburger em mobile pra abrir o drawer lateral.
export default function ResponsiveAppBar({ onMenuClick }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    // Tenta revogar refresh no servidor antes de limpar. Se der erro
    // (rede, refresh já expirado), segue: o access ainda expira sozinho
    // e a UX de logout não pode ser bloqueada por chamada de rede.
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refresh_token: refreshToken });
      } catch {
        /* ignore */
      }
    }
    localStorage.clear();
    navigate('/');
  };

  return (
    <AppBar
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      position="fixed"
      color="secondary"
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {onMenuClick ? (
            <IconButton
              color="inherit"
              aria-label="Abrir menu"
              edge="start"
              onClick={onMenuClick}
              sx={{ mr: 1, display: { xs: 'inline-flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <img src={logo} alt="logo" style={{ height: 42, display: 'block' }} />
          </Typography>
        </Box>

        <Box>
          <IconButton
            onClick={handleOpenMenu}
            size="small"
            aria-label="Menu do usuário"
            aria-controls={anchorEl ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {getInitials(user?.name)}
            </Avatar>
          </IconButton>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleCloseMenu}
            onClick={handleCloseMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }} noWrap>
                {user?.name || 'Usuário'}
              </Typography>
              {user?.email ? (
                <Typography variant="caption" sx={{ color: '#666' }} noWrap component="div">
                  {user.email}
                </Typography>
              ) : null}
              {user?.role ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#5c257e',
                    textTransform: 'capitalize',
                    fontWeight: 500,
                  }}
                  component="div"
                >
                  {user.role}
                </Typography>
              ) : null}
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

ResponsiveAppBar.propTypes = {
  onMenuClick: PropTypes.func,
};
